import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, StaggerItem, LineReveal } from "@/components/Reveal";
import { BUSINESS, AREAS } from "@/data/site";

export default function ServiceAreasPage() {
  return (
    <>
      <SEO
        title="Cleaning Services in the Kansas City Area | Julisa's House Cleaning"
        description="House cleaning and commercial cleaning in Overland Park KS, Lenexa KS, Olathe KS, Kansas City KS and Kansas City MO. Local, reliable and detail-focused. Get a free quote."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-white pt-14 pb-12 sm:pt-20 sm:pb-16">
        <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-sky-100/70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Where We Work
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            <LineReveal lines={["Cleaning Services in the", <>Kansas City <span className="text-sky-600 italic">Area</span></>]} />
          </h1>
          <p className="mt-6 max-w-3xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Julisa's House Cleaning proudly serves the Kansas City metropolitan area on both sides
            of the state line. Whether you need house cleaning in Overland Park, KS, recurring
            cleaning services in Lenexa, KS, move-out cleaning services in Olathe, KS, or a
            reliable cleaning company in Kansas City, KS or Kansas City, MO — we bring the same
            professional, detail-focused service to every visit.
          </p>
        </div>
      </section>

      <section className="bg-sky-50/70 pb-20 sm:pb-28 pt-2" aria-label="Service area locations">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((a, i) => (
              <StaggerItem key={a.slug} className={i === 4 ? "md:col-span-2 lg:col-span-1" : ""}>
                <article
                  data-testid={`service-area-card-${a.slug}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 transition-transform duration-300 group-hover:scale-110">
                      <MapPin className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="font-display text-3xl text-sky-100" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-slate-900">
                    Cleaning Services in {a.city}, {a.state}
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a.blurb}</p>
                  <Link
                    to="/quote"
                    data-testid={`area-quote-link-${a.slug}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                  >
                    Get a quote in {a.city} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </article>
              </StaggerItem>
            ))}

            <StaggerItem>
              <div className="flex h-full flex-col justify-center rounded-2xl bg-sky-600 p-8 text-white shadow-lg shadow-sky-600/25">
                <h2 className="font-display text-2xl font-semibold leading-tight">Nearby? Just ask.</h2>
                <p className="mt-3 text-sm text-sky-100 leading-relaxed">
                  We also serve surrounding communities throughout the Kansas City metro. If you're
                  close to one of our main service areas, there's a good chance we can help.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link
                    to="/quote"
                    data-testid="areas-cta-quote-button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-sky-700 transition-all duration-200 hover:bg-sky-50 active:scale-[0.98]"
                  >
                    Request a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid="areas-cta-call-button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition-all duration-200 hover:bg-white/20 active:scale-[0.98]"
                  >
                    <Phone className="h-4 w-4" aria-hidden="true" /> {BUSINESS.phone}
                  </a>
                </div>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>
    </>
  );
}
